import React, { useEffect, useState } from "react";
import PricingPlans, { type PricingCard } from "@/components/student/PricingPlans";
import * as api from "@/src/api/sdk.gen";
import { fmtDisplayPrice } from "@/lib/checkout-formatters";

/**
 * Two cards, each selling Level 1 of a course, as V1's pricing page does.
 *
 * V1's cards also promise platform-wide access through 2026, which its catalog offer provides.
 * V2 has no such offer, so these cards say only what the purchase gives: that level of that
 * course. Prices come from the plan rows checkout charges, so the page cannot quote one figure
 * and charge another.
 */
export default function StudentPricingPage() {
  const [cards, setCards] = useState<PricingCard[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.getEntryLevelPlansApiV1PaymentsEntryLevelPlansGet();
        if (!data || cancelled) return;
        setCards(
          data.plans.map((plan) => ({
            key: plan.key,
            courseTitle: plan.course_title,
            planId: plan.plan_id ?? null,
            priceFormatted:
              plan.price != null && plan.currency
                ? fmtDisplayPrice(Number(plan.price), plan.currency).primary
                : null,
            alreadyOwned: plan.already_owned ?? false,
          })),
        );
      } catch {
        // Leaves the cards showing "Not available" rather than a price we cannot stand behind.
        if (!cancelled) setCards([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <div className="min-h-screen bg-gray-50/60">
      <div className="max-w-5xl mx-auto px-4 py-10">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Pricing</h1>
          <p className="mt-1 text-sm text-gray-500">
            Start a course at Level 1 for one simple price.
          </p>
        </div>

        <PricingPlans cards={cards} />
      </div>
    </div>
  );
}
